<script setup lang="ts">
import axios from 'axios'
import { GraduationCap } from 'lucide-vue-next'

import type { PartnerOption } from '@/types/wallet'

const emit = defineEmits<{ (e: 'onCreateWallet'): void }>()

onMounted(async () => {
  await getVisiblePartners()
})

const { t } = useI18n()
const { user } = useLocalStorage()

const numberOfSteps = 4
const isModalOpen = ref(false)
const compatiblePartnersSlugs = ['lightspeed', 'zelty', 'menlog']
const partners = ref([])
const selectedPartner = ref<string | null>(null)

const getVisiblePartners = async () => {
  try {
    const { data: response } = await axios.get('partners', {
      params: {
        isVisibleInMarketplace: 1
      }
    })
    partners.value = response.data
  } catch (error) {
    throw new Error(error)
  }
}

const partnerOptions = computed<PartnerOption[]>(() => {
  const mappedPartners = partners.value
    .filter((partner) => compatiblePartnersSlugs.includes(partner.slug))
    .map(({ logo, name, slug }) => ({
      img: logo,
      title: name,
      slug: slug
    }))

  return [
    ...mappedPartners,
    {
      title: t('pages.loyaltySupports.wallet.emptyState.modal.pongoCompanionApp'),
      hasDescription: true,
      slug: 'pongo-companion-app'
    },
    {
      title: t('pages.loyaltySupports.wallet.emptyState.modal.incompatiblePartner'),
      slug: 'incompatible-partner'
    }
  ]
})

const isButtonDisabled = computed(
  () => selectedPartner.value === null || selectedPartner.value === 'incompatible-partner'
)

const walletImageUrl = computed(() => {
  return `${import.meta.env.VITE_AWS_STORAGE_URL}/dashboard/wallet/asset-empty-wallet.png`
})

const getRadioGroupAlignmentClass = (option: PartnerOption) => {
  if (option.hasDescription) {
    return 'items-start justify-start'
  }

  if (!option.img) {
    return 'justify-start'
  }

  return 'justify-center'
}
</script>

<template>
  <div class="relative h-screen">
    <div
      class="mx-auto flex w-full flex-col items-center justify-center gap-10 p-6 md:absolute md:top-1/2 md:-translate-y-2/4 md:px-12 xl:flex-row xl:items-stretch xl:gap-20"
    >
      <div class="flex items-center justify-center">
        <div>
          <h2 class="text-2xl font-extrabold text-foreground sm:text-3xl">
            {{ t('pages.loyaltySupports.wallet.title') }}
          </h2>
          <p class="text-md mb-8 mt-1 font-light text-foreground-secondary sm:text-lg">
            {{ t('pages.loyaltySupports.wallet.description') }}
          </p>
          <div class="rounded-md border bg-white p-6">
            <p class="mb-4 font-extrabold text-foreground">{{ t('pages.loyaltySupports.wallet.emptyState.title') }}</p>
            <ol class="flex flex-col gap-4">
              <li
                v-for="step in numberOfSteps"
                :key="step"
                class="flex max-w-2xl items-center gap-4 text-sm text-foreground"
              >
                <BaseChip color="violet" :text="step" />
                <i18n-t v-if="step === 1" keypath="pages.loyaltySupports.wallet.emptyState.steps.1.text" tag="span">
                  <template #url>
                    <a :href="`https://${user.merchant.webAppUrl}`" target="_blank" class="text-primary">
                      {{ t('pages.loyaltySupports.wallet.emptyState.steps.1.link') }}
                    </a>
                  </template>
                </i18n-t>
                <span v-else>
                  {{ t(`pages.loyaltySupports.wallet.emptyState.steps.${step}`) }}
                </span>
              </li>
            </ol>
            <BaseTip class="mt-6" severity="info" :text="t('pages.loyaltySupports.wallet.emptyState.tip')" />
          </div>
          <div class="mt-8 flex flex-col items-center gap-4 sm:flex-row">
            <Button variant="default-primary" @click.prevent="isModalOpen = true">
              {{ t('pages.loyaltySupports.wallet.emptyState.create') }}
            </Button>
            <Button as-child variant="ghost-primary">
              <a href="https://content.heypongo.com/produit/01_Wallet.mp4" target="_blank" rel="noopener noreferrer">
                <GraduationCap />
                {{ t('pages.loyaltySupports.wallet.emptyState.learnMore') }}
              </a>
            </Button>
          </div>
        </div>
      </div>
      <div class="mx-auto hidden flex-1 xl:mx-0 xl:block 2xl:max-w-md">
        <img :src="walletImageUrl" class="h-full object-contain" alt="Wallet empty state" />
      </div>
      <BaseModal
        :is-open="isModalOpen"
        :is-disabled="isButtonDisabled"
        :cancel="t('global.words.cancel')"
        :heading-title="t('pages.loyaltySupports.wallet.emptyState.modal.title')"
        :link-text="t('pages.loyaltySupports.wallet.emptyState.modal.confirm')"
        size="2xl"
        @click="emit('onCreateWallet')"
        @close="isModalOpen = false"
      >
        <AppRadioCard
          v-model="selectedPartner"
          :options="partnerOptions.map((item) => item.slug)"
          class="grid grid-cols-2 gap-4"
        >
          <template v-for="(option, index) in partnerOptions" #[index] :key="index">
            <div class="flex h-12 items-center space-x-4 text-sm font-medium text-foreground">
              <img v-if="option.img" :src="option.img" class="h-10 w-10" />
              <div class="flex h-full flex-col space-y-1.5" :class="getRadioGroupAlignmentClass(option)">
                <label class="hover:cursor-pointer">{{ option.title }}</label>
                <i18n-t
                  v-if="option.hasDescription"
                  keypath="pages.loyaltySupports.wallet.emptyState.modal.learnMore.text"
                  tag="p"
                  class="text-xs font-normal text-foreground-secondary"
                >
                  <template #url>
                    <a
                      href="https://content.heypongo.com/produit/02_App_Compagnon.mp4"
                      target="_blank"
                      class="text-primary underline"
                    >
                      {{ t('pages.loyaltySupports.wallet.emptyState.modal.learnMore.link') }}
                    </a>
                  </template>
                </i18n-t>
              </div>
            </div>
          </template>
        </AppRadioCard>
      </BaseModal>
    </div>
  </div>
</template>
